import React from 'react';
import { Link } from '@inertiajs/react';
import { Tag, Typography } from 'antd';
import Markdown from '../components/Markdown';
import LinkedTasks from '../components/worklog/LinkedTasks';
import LinkedWikis from '../components/worklog/LinkedWikis';
import { WORKLOG_TASK_TYPE_LABELS } from '../types/worklog';
import type { WorklogFull } from '../types/worklog';

// /worklog/:slug —— 单条日志页。
//
// 存在的理由是「有地址」，不是「多一种读法」：抽屉开在哪一条是 pages/Worklog.tsx
// 的组件 state，不进 URL，于是站内任何地方都跳不进某一条日志。
// 这条规矩 Worklog.tsx:12 与 wikis_controller.rb:74 都立过：URL 决定内容。
//
// 正文用 size="article"（行宽 720px）而不是抽屉那档 compact：body_md 是按 worklog
// 模板写的长文，抽屉的宽度是为「扫一眼」调的。页面宽度跟着 Markdown 的 article
// 档走同一个 720px，不然正文的行宽由外层容器说话，两处各写一个数就会开始漂。
//
// 刻意没有删除按钮：删除是列表页抽屉那边的动作。同一个破坏性动作放两处，就要在
// 两处各维护一遍确认框和「删完往哪跳」，而这一页的职责只是读。
type WorklogPageProps = {
  worklog: WorklogFull;
};

const WorklogPage: React.FC<WorklogPageProps> = ({ worklog }) => (
  <div className="mx-auto w-full max-w-[720px] px-4 py-6">
    {/* 回列表页。这一页多半是从别处跳进来的（任务右栏的「相关日志」），
        没有这一步就走不到列表。 */}
    <Link href="/worklog" className="text-sm text-blue-600">
      ← 工作日志
    </Link>

    <h1 className="mt-3 mb-2 break-words text-2xl font-bold">{worklog.title}</h1>

    <p className="mb-2 break-words text-sm text-gray-600">{worklog.summary}</p>

    <div className="mb-6 flex flex-col gap-1">
      <div>
        <Tag>{WORKLOG_TASK_TYPE_LABELS[worklog.task_type] ?? worklog.task_type}</Tag>
        <Typography.Text type="secondary">
          {new Date(worklog.completed_at).toLocaleString('zh-CN', { hour12: false })}
        </Typography.Text>
      </div>
      {/* 工作目录是这个子系统的项目维度（worklogs 表不挂 project 外键）。 */}
      <Typography.Text type="secondary" className="break-all text-xs">
        {worklog.working_directory ?? '未记录工作目录'}
      </Typography.Text>
      <Typography.Text type="secondary" className="break-all text-xs">
        {worklog.slug}
      </Typography.Text>
    </div>

    {/* 这次会话干了哪几条任务。位置在摘要和正文之间：读日志的人先想知道
        「这是关于什么的」，再读细节。没有关联时组件自己返回 null。 */}
    <LinkedTasks tasks={worklog.linked_tasks} />
    <LinkedWikis wikis={worklog.linked_wikis} />

    {/* body_md 可以是 nil（老条目、或写入时没给正文）。那不是错误状态。 */}
    {worklog.body_md ? (
      <Markdown size="article">{worklog.body_md}</Markdown>
    ) : (
      <Typography.Text type="secondary">这条日志没有正文。</Typography.Text>
    )}
  </div>
);

export default WorklogPage;
